/**
 * unifiedBetCardStyles.js — static StyleSheet extracted from UnifiedBetCard.jsx
 * (god-file decomposition). Purely presentational constants; no logic.
 */

import { StyleSheet, Platform } from 'react-native';
import { uiVar } from './floodlight/uiStyleCss';

export const styles = StyleSheet.create({
    card: {
        width: '100%',
        alignSelf: 'stretch',
        borderRadius: 16,
        overflow: 'hidden',
        marginBottom: 12,
        borderWidth: 1,
        borderColor: uiVar('rs-line-card-rule', 'rgba(255,255,255,0.08)'),
        ...(Platform.OS === 'web' ? {
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
        } : {
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.3,
            shadowRadius: 10,
            elevation: 8,
        }),
    },
    cardSelected: {
        borderColor: uiVar('rs-line-selected', 'rgba(99, 102, 241, 0.5)'),
        borderWidth: 2,
    },
    cardSGP: {
        borderColor: uiVar('rs-line-ink', 'rgba(139, 92, 246, 0.4)'),
        borderWidth: 2,
    },
    cardLive: {
        borderColor: uiVar('rs-line-pos', 'rgba(34, 197, 94, 0.5)'),  // ⭐ GREEN for LIVE (#22C55E)
        borderWidth: 2,
    },
    cardEnded: {
        opacity: 0.6,
    },
    // Two-column layout
    twoColumnLayout: {
        flexDirection: 'row',
        position: 'relative',
    },
    leftColumn: {
        flex: 1,
        minWidth: 0,
        paddingRight: 280, // Reserve space for absolute right column (wider on desktop)
    },
    rightColumn: {
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        width: 280, // Wider on desktop for better readability
        borderLeftWidth: 1,
        borderLeftColor: uiVar('rs-line-hair', 'rgba(255,255,255,0.08)'),
        backgroundColor: uiVar('rs-fill-sunken', 'rgba(0,0,0,0.15)'),
        display: 'flex',
        flexDirection: 'column',
    },
    
    // SGP Badge
    sgpBadge: {
        position: 'absolute',
        top: 12,
        right: 12,
        zIndex: 10,
    },
    sgpBadgeGradient: {
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 8,
    },
    sgpBadgeText: {
        color: uiVar('rs-text-on-active', '#fff'),
        fontSize: 11,
        fontWeight: '800',
        letterSpacing: 1,
    },
    
    // Header
    header: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        padding: 16,
        paddingBottom: 12,
        borderBottomWidth: 1,
        borderBottomColor: uiVar('rs-line-hair', 'rgba(255,255,255,0.06)'),
    },
    photoContainer: {
        width: 52,
        height: 52,
        borderRadius: 26,
        overflow: 'hidden',
        backgroundColor: uiVar('rs-fill-wash3', 'rgba(255,255,255,0.1)'),
        marginRight: 12,
        // ⭐ Web fix: Ensure container doesn't duplicate/stretch image
        ...(Platform.OS === 'web' ? {
            position: 'relative',
            flexShrink: 0,
        } : {}),
    },
    photo: {
        width: '100%',
        height: '100%',
        resizeMode: 'cover',
        // ⭐ Web fix: Proper image scaling to prevent warped/layered appearance
        ...(Platform.OS === 'web' ? {
            objectFit: 'cover',
            objectPosition: 'center',
        } : {}),
    },
    teamLogosSmall: {
        width: '100%',
        height: '100%',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
    },
    teamLogoSmall: {
        width: 22,
        height: 22,
        borderRadius: 4,
    },
    initialsContainer: {
        width: '100%',
        height: '100%',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: uiVar('rs-fill-solid', 'rgba(99, 102, 241, 0.3)'),
    },
    initialsText: {
        color: uiVar('rs-text-on-active', '#fff'),
        fontSize: 16,
        fontWeight: '700',
    },
    headerInfo: {
        flex: 1,
        paddingRight: 60,
    },
    titleRow: {
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 8,
    },
    title: {
        color: uiVar('rs-text-ink', '#fff'),
        fontSize: 16,
        fontWeight: '700',
    },
    description: {
        color: uiVar('rs-text-ink2', 'rgba(255,255,255,0.6)'),
        fontSize: 13,
        marginTop: 4,
    },
    dateTimeRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        marginTop: 8,
        flexWrap: 'wrap',
    },
    // ⭐ Status Badge Styles (LIVE, SOON, UPCOMING, FINAL)
    statusBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 6,
        gap: 4,
    },
    statusBadgeText: {
        fontSize: 10,
        fontWeight: '700',
        letterSpacing: 0.5,
    },
    liveDot: {
        width: 6,
        height: 6,
        borderRadius: 3,
        backgroundColor: uiVar('rs-fill-pos', '#22C55E'),  // ⭐ GREEN for LIVE
    },
    dateText: {
        color: uiVar('rs-text-accent', 'rgba(162, 89, 255, 0.95)'),
        fontSize: 12,
        fontWeight: '600',
    },
    timeText: {
        color: uiVar('rs-text-ink2', 'rgba(255,255,255,0.7)'),
        fontSize: 12,
        fontWeight: '500',
    },
    noDateText: {
        color: uiVar('rs-text-muted', 'rgba(255,255,255,0.4)'),
        fontSize: 11,
        fontStyle: 'italic',
    },
    liveBadge: {
        backgroundColor: uiVar('rs-fill-pos-wash', 'rgba(34, 197, 94, 0.2)'),  // ⭐ GREEN for LIVE
        paddingHorizontal: 6,
        paddingVertical: 2,
        borderRadius: 4,
    },
    liveBadgeText: {
        color: uiVar('rs-text-pos', '#22C55E'),  // ⭐ GREEN for LIVE
        fontSize: 10,
        fontWeight: '700',
    },
    evContainer: {
        position: 'absolute',
        top: 16,
        right: 16,
        alignItems: 'center',
    },
    evValue: {
        color: uiVar('rs-text-ink2', 'rgba(255,255,255,0.7)'),
        fontSize: 18,
        fontWeight: '700',
    },
    evPositive: {
        color: uiVar('rs-text-pos', '#10B981'),
    },
    evNegative: {
        color: uiVar('rs-text-neg', '#EF4444'),
    },
    oddsPositive: {
        color: uiVar('rs-text-pos', '#10B981'),
    },
    oddsNegative: {
        color: uiVar('rs-text-neg', '#EF4444'),
    },
    evLabel: {
        color: uiVar('rs-text-muted', 'rgba(255,255,255,0.4)'),
        fontSize: 10,
        fontWeight: '600',
    },
    
    // Main/Alt Badge
    mainAltBadge: {
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 4,
    },
    mainBadge: {
        backgroundColor: uiVar('rs-fill-pos', '#2E7D32'),
    },
    altBadge: {
        backgroundColor: uiVar('rs-fill-warn', '#E65100'),
    },
    mainAltBadgeText: {
        color: uiVar('rs-text-on-badge', '#fff'),
        fontSize: 9,
        fontWeight: '800',
        letterSpacing: 0.5,
    },
    // 🏥 Injury Badge Styles
    injuryBadge: {
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 4,
        backgroundColor: uiVar('rs-fill-warn-wash', 'rgba(251, 191, 36, 0.2)'),
        borderWidth: 1,
        borderColor: uiVar('rs-line-warn', 'rgba(251, 191, 36, 0.5)'),
    },
    injuryBadgeOut: {
        backgroundColor: uiVar('rs-fill-neg-wash', 'rgba(239, 68, 68, 0.2)'),
        borderColor: uiVar('rs-line-neg', 'rgba(239, 68, 68, 0.6)'),
    },
    injuryBadgeDoubtful: {
        backgroundColor: uiVar('rs-fill-neg-wash', 'rgba(239, 68, 68, 0.15)'),
        borderColor: uiVar('rs-line-neg', 'rgba(239, 68, 68, 0.4)'),
    },
    injuryBadgeQuestionable: {
        backgroundColor: uiVar('rs-fill-warn-wash', 'rgba(251, 191, 36, 0.2)'),
        borderColor: uiVar('rs-line-warn', 'rgba(251, 191, 36, 0.5)'),
    },
    injuryBadgeDayToDay: {
        backgroundColor: uiVar('rs-fill-warn-wash', 'rgba(251, 191, 36, 0.15)'),
        borderColor: uiVar('rs-line-warn', 'rgba(251, 191, 36, 0.4)'),
    },
    injuryBadgeText: {
        color: uiVar('rs-text-ink', '#fff'),
        fontSize: 9,
        fontWeight: '700',
        letterSpacing: 0.5,
    },
    sportsbookBadge: {
        backgroundColor: uiVar('rs-fill-select', 'rgba(162, 89, 255, 0.2)'),
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: uiVar('rs-line-ink', 'rgba(162, 89, 255, 0.5)'),
    },
    sportsbookBadgeText: {
        color: uiVar('rs-text-accent', 'rgba(162, 89, 255, 0.95)'),
        fontSize: 10,
        fontWeight: '700',
    },
    
    // Price Section
    priceSection: {
        flexDirection: 'row',
        alignItems: 'center',
        padding: 16,
        backgroundColor: uiVar('rs-fill-wash1', 'rgba(255,255,255,0.03)'),
        borderRadius: 10,
        margin: 12,
        marginTop: 8,
    },
    lineSelected: {
        backgroundColor: uiVar('rs-fill-select', 'rgba(99, 102, 241, 0.15)'),
        borderWidth: 1,
        borderColor: uiVar('rs-line-ink', 'rgba(99, 102, 241, 0.3)'),
    },
    priceLeft: {
        flex: 1,
    },
    lineDisplayRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    lineValue: {
        color: uiVar('rs-text-ink', '#fff'),
        fontSize: 22,
        fontWeight: '700',
    },
    mainBadgeSmall: {
        backgroundColor: uiVar('rs-fill-pos-wash', 'rgba(46, 125, 50, 0.3)'),
        paddingHorizontal: 6,
        paddingVertical: 2,
        borderRadius: 4,
    },
    mainBadgeSmallText: {
        color: uiVar('rs-text-pos', '#4CAF50'),
        fontSize: 9,
        fontWeight: '700',
    },
    // Yes/No badges for binary markets
    yesNoBadge: {
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 6,
        marginLeft: 8,
    },
    yesBadge: {
        backgroundColor: uiVar('rs-fill-pos-wash', 'rgba(16, 185, 129, 0.25)'),
        borderWidth: 1,
        borderColor: uiVar('rs-line-pos', 'rgba(16, 185, 129, 0.5)'),
    },
    noBadge: {
        backgroundColor: uiVar('rs-fill-neg-wash', 'rgba(239, 68, 68, 0.25)'),
        borderWidth: 1,
        borderColor: uiVar('rs-line-neg', 'rgba(239, 68, 68, 0.5)'),
    },
    yesNoBadgeText: {
        color: uiVar('rs-text-ink', '#fff'),
        fontSize: 12,
        fontWeight: '700',
    },
    bookCountText: {
        color: uiVar('rs-text-muted', 'rgba(255,255,255,0.5)'),
        fontSize: 11,
        marginTop: 2,
    },
    priceRight: {
        alignItems: 'flex-end',
        marginRight: 16,
    },
    priceValue: {
        color: uiVar('rs-text-ink', '#fff'),
        fontSize: 22,
        fontWeight: '700',
    },
    impliedText: {
        color: uiVar('rs-text-muted', 'rgba(255,255,255,0.5)'),
        fontSize: 11,
        marginTop: 2,
    },
    stackedLogos: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    stackedLogo: {
        width: 28,
        height: 28,
        borderRadius: 14,
        borderWidth: 2,
        borderColor: uiVar('rs-line-card', 'rgba(30, 30, 45, 1)'),
    },
    moreLogos: {
        width: 28,
        height: 28,
        borderRadius: 14,
        backgroundColor: uiVar('rs-fill-wash3', 'rgba(255,255,255,0.1)'),
        justifyContent: 'center',
        alignItems: 'center',
        borderWidth: 2,
        borderColor: uiVar('rs-line-card', 'rgba(30, 30, 45, 1)'),
    },
    moreLogosText: {
        color: uiVar('rs-text-ink2', 'rgba(255,255,255,0.7)'),
        fontSize: 9,
        fontWeight: '700',
    },
    
    // Side switch (Over | Under, − | +) — one card holds both sides
    sideSwitchRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        paddingHorizontal: 16,
        paddingVertical: 8,
        borderTopWidth: 1,
        borderTopColor: uiVar('rs-line-hair', 'rgba(255,255,255,0.06)'),
    },
    sideSwitchBtn: {
        minWidth: 56,
        minHeight: 32,
        paddingHorizontal: 12,
        alignItems: 'center',
        justifyContent: 'center',
    },
    sideSwitchText: {
        fontSize: 12,
        fontWeight: '700',
    },

    // Alt Toggle
    altToggle: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 16,
        paddingVertical: 10,
        borderTopWidth: 1,
        borderTopColor: uiVar('rs-line-hair', 'rgba(255,255,255,0.06)'),
    },
    altToggleLeft: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    altBadgeSmall: {
        backgroundColor: uiVar('rs-fill-warn-wash', 'rgba(249, 115, 22, 0.2)'),
        paddingHorizontal: 6,
        paddingVertical: 2,
        borderRadius: 4,
        marginRight: 8,
    },
    altBadgeSmallText: {
        color: uiVar('rs-text-warn', '#F97316'),
        fontSize: 9,
        fontWeight: '800',
    },
    altToggleText: {
        color: uiVar('rs-text-ink2', 'rgba(255,255,255,0.6)'),
        fontSize: 12,
    },
    
    // Alt Lines Section - Increased height for better visibility
    altLinesSection: {
        borderTopWidth: 1,
        borderTopColor: uiVar('rs-line-hair', 'rgba(255,255,255,0.06)'),
        maxHeight: 320,
        paddingHorizontal: 12,
        paddingVertical: 8,
    },
    // ⭐ Empty filter state
    emptyFilterState: {
        padding: 24,
        alignItems: 'center',
        justifyContent: 'center',
        borderTopWidth: 1,
        borderTopColor: uiVar('rs-line-hair', 'rgba(255,255,255,0.06)'),
    },
    emptyFilterText: {
        color: uiVar('rs-text-muted', 'rgba(255,255,255,0.5)'),
        fontSize: 13,
        marginTop: 8,
        marginBottom: 12,
    },
    clearFiltersBtn: {
        paddingHorizontal: 16,
        paddingVertical: 8,
        backgroundColor: uiVar('rs-fill-wash3', 'rgba(255,255,255,0.1)'),
        borderRadius: 6,
    },
    clearFiltersBtnText: {
        color: uiVar('rs-text-ink', '#fff'),
        fontSize: 12,
        fontWeight: '600',
    },
    // ⭐ Line Controls Section - Compact layout
    lineControlsSection: {
        backgroundColor: uiVar('rs-fill-sunken', 'rgba(0,0,0,0.2)'),
        paddingHorizontal: 10,
        paddingVertical: 6,
        borderTopWidth: 1,
        borderTopColor: uiVar('rs-line-hair', 'rgba(255,255,255,0.06)'),
    },
    lineControlsTopRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 8,
    },
    lineControlsLeft: {
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 12,
        flex: 1,
    },
    lineControlInline: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
    lineControlLabelInline: {
        color: uiVar('rs-text-muted', 'rgba(255,255,255,0.5)'),
        fontSize: 10,
        fontWeight: '600',
    },
    lineControlButtonsInline: {
        flexDirection: 'row',
        gap: 3,
    },
    lineControlRow: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 8,
    },
    lineControlLabel: {
        color: uiVar('rs-text-muted', 'rgba(255,255,255,0.5)'),
        fontSize: 11,
        fontWeight: '600',
        minWidth: 70,
    },
    lineControlButtons: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 4,
        flex: 1,
    },
    lineControlBtn: {
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 5,
        backgroundColor: uiVar('rs-fill-wash2', 'rgba(255,255,255,0.08)'),
        borderWidth: 1,
        borderColor: uiVar('rs-line-hair', 'rgba(255,255,255,0.1)'),
    },
    lineControlBtnSmall: {
        paddingHorizontal: 6,
        paddingVertical: 3,
    },
    lineControlBtnHover: {
        backgroundColor: uiVar('rs-fill-wash3', 'rgba(255,255,255,0.12)'),
        borderColor: uiVar('rs-line-hair2', 'rgba(255,255,255,0.18)'),
    },
    rowHover: {
        backgroundColor: uiVar('rs-fill-wash1', 'rgba(255,255,255,0.03)'),
    },
    lineControlBtnActive: {
        backgroundColor: uiVar('rs-fill-solid', 'rgba(99, 102, 241, 0.3)'),
        borderColor: uiVar('rs-line-ink', 'rgba(99, 102, 241, 0.5)'),
    },
    lineControlBtnText: {
        color: uiVar('rs-text-ink2', 'rgba(255,255,255,0.6)'),
        fontSize: 9,
        fontWeight: '600',
    },
    lineControlBtnTextActive: {
        color: uiVar('rs-text-on-active', '#fff'),
    },
    // Main Only Toggle - Compact right-aligned
    mainOnlyToggle: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 6,
        backgroundColor: uiVar('rs-fill-wash2', 'rgba(255,255,255,0.05)'),
    },
    mainOnlyToggleActive: {
        backgroundColor: uiVar('rs-fill-select', 'rgba(99, 102, 241, 0.15)'),
    },
    mainOnlyToggleText: {
        color: uiVar('rs-text-muted', 'rgba(255,255,255,0.5)'),
        fontSize: 9,
        fontWeight: '600',
    },
    mainOnlyToggleTextActive: {
        color: uiVar('rs-text-ink', '#fff'),
    },
    toggleSwitchSmall: {
        width: 28,
        height: 16,
        borderRadius: 8,
        backgroundColor: uiVar('rs-fill-wash4', 'rgba(255,255,255,0.15)'),
        padding: 2,
        justifyContent: 'center',
    },
    toggleSwitchSmallActive: {
        backgroundColor: uiVar('rs-fill-pos', 'rgba(99, 102, 241, 0.5)'),
    },
    toggleKnobSmall: {
        width: 12,
        height: 12,
        borderRadius: 6,
        backgroundColor: uiVar('rs-fill-wash5', 'rgba(255,255,255,0.6)'),
    },
    toggleKnobSmallActive: {
        backgroundColor: uiVar('rs-fill-on-pos', '#fff'),
        marginLeft: 'auto',
    },
    lineControlToggle: {
        justifyContent: 'space-between',
        paddingVertical: 6,
        paddingHorizontal: 4,
        borderRadius: 6,
        marginBottom: 0,
    },
    lineControlToggleActive: {
        backgroundColor: uiVar('rs-fill-select', 'rgba(99, 102, 241, 0.1)'),
    },
    toggleSwitch: {
        width: 36,
        height: 20,
        borderRadius: 10,
        backgroundColor: uiVar('rs-fill-wash4', 'rgba(255,255,255,0.15)'),
        padding: 2,
        justifyContent: 'center',
    },
    toggleSwitchActive: {
        backgroundColor: uiVar('rs-fill-pos', 'rgba(99, 102, 241, 0.5)'),
    },
    toggleKnob: {
        width: 16,
        height: 16,
        borderRadius: 8,
        backgroundColor: uiVar('rs-fill-wash5', 'rgba(255,255,255,0.6)'),
    },
    toggleKnobActive: {
        backgroundColor: uiVar('rs-fill-on-pos', '#fff'),
        marginLeft: 'auto',
    },
    altLineRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingVertical: 10,
        paddingHorizontal: 12,
        borderRadius: 8,
        marginBottom: 4,
        backgroundColor: uiVar('rs-fill-wash1', 'rgba(255,255,255,0.02)'),
    },
    altLineRowHovered: {
        backgroundColor: uiVar('rs-fill-wash2', 'rgba(255,255,255,0.06)'),
    },
    selectedAltLineRow: {
        backgroundColor: uiVar('rs-fill-select', 'rgba(139, 92, 246, 0.15)'),
        borderWidth: 1,
        borderColor: uiVar('rs-line-ink', 'rgba(139, 92, 246, 0.3)'),
    },
    altLineLeft: {
        flex: 1,
    },
    altLineValue: {
        color: uiVar('rs-text-ink', 'rgba(255,255,255,0.9)'),
        fontSize: 15,
        fontWeight: '600',
    },
    altLineBooks: {
        color: uiVar('rs-text-muted', 'rgba(255,255,255,0.4)'),
        fontSize: 10,
        marginTop: 2,
    },
    altLineMetaRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    highBookCount: {
        color: uiVar('rs-text-pos', '#10B981'),
        fontWeight: '600',
    },
    lowBookCount: {
        color: uiVar('rs-text-warn', '#F59E0B'),
    },
    // ⭐ Requested line highlighting
    requestedLineHighlight: {
        backgroundColor: uiVar('rs-fill-pos-wash', 'rgba(16, 185, 129, 0.15)'),
        borderWidth: 1,
        borderColor: uiVar('rs-line-pos', 'rgba(16, 185, 129, 0.4)'),
    },
    requestedLineText: {
        color: uiVar('rs-text-pos', '#10B981'),
        fontWeight: '700',
    },
    requestedBadge: {
        backgroundColor: uiVar('rs-fill-pos-wash', 'rgba(16, 185, 129, 0.25)'),
        paddingHorizontal: 6,
        paddingVertical: 2,
        borderRadius: 4,
        borderWidth: 1,
        borderColor: uiVar('rs-line-pos', 'rgba(16, 185, 129, 0.5)'),
    },
    requestedBadgeText: {
        color: uiVar('rs-text-pos', '#10B981'),
        fontSize: 8,
        fontWeight: '800',
        letterSpacing: 0.5,
    },
    altLinePrice: {
        color: uiVar('rs-text-ink', 'rgba(255,255,255,0.9)'),
        fontSize: 15,
        fontWeight: '600',
    },
    altLineValueRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
    },
    lineBadge: {
        paddingHorizontal: 5,
        paddingVertical: 2,
        borderRadius: 3,
    },
    mainBadgeMini: {
        backgroundColor: uiVar('rs-fill-pos-wash', 'rgba(46, 125, 50, 0.25)'),
    },
    altBadgeMini: {
        backgroundColor: uiVar('rs-fill-warn-wash', 'rgba(230, 81, 0, 0.25)'),
    },
    lineBadgeText: {
        fontSize: 8,
        fontWeight: '700',
        color: uiVar('rs-text-ink2', 'rgba(255,255,255,0.7)'),
    },
    
    // SGP Legs
    sgpLegsSection: {
        borderTopWidth: 1,
        borderTopColor: uiVar('rs-line-hair', 'rgba(255,255,255,0.06)'),
        padding: 12,
    },
    sgpLegsTitle: {
        color: uiVar('rs-text-muted', 'rgba(255,255,255,0.5)'),
        fontSize: 11,
        fontWeight: '600',
        marginBottom: 8,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    sgpLegRow: {
        paddingVertical: 6,
        borderBottomWidth: 1,
        borderBottomColor: uiVar('rs-line-hair', 'rgba(255,255,255,0.04)'),
    },
    sgpLegMarket: {
        color: uiVar('rs-text-muted', 'rgba(255,255,255,0.5)'),
        fontSize: 10,
    },
    sgpLegSelection: {
        color: uiVar('rs-text-ink', '#fff'),
        fontSize: 13,
        fontWeight: '500',
    },
    
    // EV Methods Section
    evMethodsSection: {
        padding: 12,
        backgroundColor: uiVar('rs-fill-sunken', 'rgba(0,0,0,0.15)'),
        borderTopWidth: 1,
        borderTopColor: uiVar('rs-line-hair', 'rgba(255,255,255,0.06)'),
    },
    evMethodsTitle: {
        color: uiVar('rs-text-muted', 'rgba(255,255,255,0.5)'),
        fontSize: 10,
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        marginBottom: 8,
    },
    evMethodsRow: {
        flexDirection: 'row',
    },
    evMethodCol: {
        flex: 1,
        alignItems: 'center',
    },
    evMethodLabel: {
        color: uiVar('rs-text-muted', 'rgba(255,255,255,0.4)'),
        fontSize: 10,
        marginBottom: 4,
    },
    evMethodValue: {
        color: uiVar('rs-text-ink', '#fff'),
        fontSize: 16,
        fontWeight: '700',
    },
    evMethodUsed: {
        color: uiVar('rs-text-pos', '#10B981'),
        fontSize: 9,
        fontWeight: '600',
        marginTop: 2,
    },
    
    // Sportsbooks (Right Column)
    sportsbooksHeader: {
        flexDirection: 'column',
        alignItems: 'stretch',
        padding: 10,
        paddingBottom: 8,
        borderBottomWidth: 1,
        borderBottomColor: uiVar('rs-line-hair', 'rgba(255,255,255,0.06)'),
        gap: 6,
    },
    sportsbooksTitle: {
        color: uiVar('rs-text-ink2', 'rgba(255,255,255,0.7)'),
        fontSize: 12,
        fontWeight: '600',
        textAlign: 'center',
    },
    sortButtonsRow: {
        flexDirection: 'row',
        justifyContent: 'center',
        gap: 4,
        flexWrap: 'wrap',
    },
    sortButton: {
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 4,
        backgroundColor: uiVar('rs-fill-wash2', 'rgba(255,255,255,0.05)'),
    },
    sortButtonActive: {
        backgroundColor: uiVar('rs-fill-solid', 'rgba(99, 102, 241, 0.3)'),
    },
    sortButtonHovered: {
        backgroundColor: uiVar('rs-fill-wash3', 'rgba(255,255,255,0.12)'),
    },
    sortButtonText: {
        color: uiVar('rs-text-muted', 'rgba(255,255,255,0.5)'),
        fontSize: 10,
        fontWeight: '600',
    },
    sortButtonTextActive: {
        color: uiVar('rs-text-on-active', '#fff'),
    },
    sportsbooksList: {
        flex: 1,
    },
    sportsbookRow: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 8,
        paddingHorizontal: 12,
        borderBottomWidth: 1,
        borderBottomColor: uiVar('rs-line-hair', 'rgba(255,255,255,0.04)'),
    },
    sportsbookHighlighted: {
        backgroundColor: uiVar('rs-fill-select', 'rgba(162, 89, 255, 0.15)'),
    },
    sportsbookRowHovered: {
        backgroundColor: uiVar('rs-fill-wash2', 'rgba(255,255,255,0.07)'),
    },
    sportsbookClickable: {
        backgroundColor: uiVar('rs-fill-pos-wash', 'rgba(16, 185, 129, 0.05)'),
    },
    sportsbookSelected: {
        backgroundColor: uiVar('rs-fill-select', 'rgba(99, 102, 241, 0.2)'),
        borderLeftWidth: 3,
        borderLeftColor: uiVar('rs-line-ink', '#6366F1'),
    },
    bookSelectedIndicator: {
        marginRight: 6,
    },
    sportsbookNameSelected: {
        color: uiVar('rs-text-ink', '#fff'),
        fontWeight: '600',
    },
    sportsbookLogo: {
        width: 28,
        height: 28,
        borderRadius: 6,
        marginRight: 10,
    },
    sportsbookLogoPlaceholder: {
        width: 28,
        height: 28,
        borderRadius: 6,
        backgroundColor: uiVar('rs-fill-wash3', 'rgba(255,255,255,0.1)'),
        marginRight: 10,
        justifyContent: 'center',
        alignItems: 'center',
    },
    sportsbookLogoText: {
        color: uiVar('rs-text-muted', 'rgba(255,255,255,0.5)'),
        fontSize: 12,
        fontWeight: '700',
    },
    sportsbookInfo: {
        flex: 1,
        minWidth: 0,
    },
    sportsbookNameRow: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    sportsbookName: {
        color: uiVar('rs-text-ink2', 'rgba(255,255,255,0.8)'),
        fontSize: 12,
        marginBottom: 2,
    },
    sportsbookNameClickable: {
        color: uiVar('rs-text-pos', '#10B981'),
    },
    sportsbookNameSelected: {
        color: uiVar('rs-text-ink', '#fff'),
        fontWeight: '600',
    },
    sportsbookPrice: {
        color: uiVar('rs-text-ink', '#fff'),
        fontSize: 14,
        fontWeight: '700',
    },
    sportsbookEvContainer: {
        marginLeft: 8,
        minWidth: 45,
        alignItems: 'flex-end',
    },
    sportsbookEvValue: {
        color: uiVar('rs-text-muted', 'rgba(255,255,255,0.5)'),
        fontSize: 11,
        fontWeight: '600',
    },
    betLinkIndicator: {
        width: 24,
        height: 24,
        borderRadius: 12,
        backgroundColor: uiVar('rs-fill-pos-wash', 'rgba(16, 185, 129, 0.15)'),
        justifyContent: 'center',
        alignItems: 'center',
        marginLeft: 4,
    },
    noLinkIndicator: {
        width: 24,
        height: 24,
        justifyContent: 'center',
        alignItems: 'center',
        marginLeft: 4,
    },
    noLinkText: {
        color: uiVar('rs-text-faint', 'rgba(255,255,255,0.2)'),
        fontSize: 12,
    },
    scrollIndicator: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
        paddingVertical: 6,
        borderTopWidth: 1,
        borderTopColor: uiVar('rs-line-hair', 'rgba(255,255,255,0.06)'),
        backgroundColor: uiVar('rs-fill-sunken', 'rgba(0,0,0,0.2)'),
    },
    scrollIndicatorText: {
        color: uiVar('rs-text-muted', 'rgba(255,255,255,0.4)'),
        fontSize: 10,
        fontWeight: '500',
    },
    
    // Legacy sportsbooks toggle (for mobile)
    sportsbooksToggle: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        paddingVertical: 10,
        borderTopWidth: 1,
        borderTopColor: uiVar('rs-line-hair', 'rgba(255,255,255,0.06)'),
    },
    sportsbooksToggleText: {
        color: uiVar('rs-text-muted', 'rgba(255,255,255,0.5)'),
        fontSize: 12,
    },
    
    // Math Section
    mathToggle: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        paddingVertical: 10,
        borderTopWidth: 1,
        borderTopColor: uiVar('rs-line-hair', 'rgba(255,255,255,0.06)'),
    },
    mathToggleHovered: {
        backgroundColor: uiVar('rs-fill-wash1', 'rgba(255,255,255,0.04)'),
    },
    mathToggleText: {
        color: uiVar('rs-text-muted', 'rgba(255,255,255,0.5)'),
        fontSize: 12,
    },
    mathSection: {
        padding: 12,
        backgroundColor: uiVar('rs-fill-sunken', 'rgba(0,0,0,0.2)'),
    },
    mathRow: {
        flexDirection: 'row',
        marginBottom: 12,
    },
    mathCol: {
        flex: 1,
        alignItems: 'center',
    },
    mathLabel: {
        color: uiVar('rs-text-muted', 'rgba(255,255,255,0.4)'),
        fontSize: 10,
        marginBottom: 4,
        textTransform: 'uppercase',
    },
    mathValue: {
        color: uiVar('rs-text-ink', '#fff'),
        fontSize: 16,
        fontWeight: '600',
    },
    mathSub: {
        color: uiVar('rs-text-muted', 'rgba(255,255,255,0.4)'),
        fontSize: 10,
        marginTop: 2,
    },
    stakeInput: {
        backgroundColor: uiVar('rs-fill-wash3', 'rgba(255,255,255,0.1)'),
        color: uiVar('rs-text-ink', '#fff'),
        fontSize: 16,
        fontWeight: '600',
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 6,
        textAlign: 'center',
        minWidth: 60,
    },
    
    // Team Logos
    teamLogosRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        paddingVertical: 12,
        borderTopWidth: 1,
        borderTopColor: uiVar('rs-line-hair', 'rgba(255,255,255,0.06)'),
    },
    teamLogo: {
        width: 36,
        height: 36,
        borderRadius: 8,
    },
    vsText: {
        color: uiVar('rs-text-muted', 'rgba(255,255,255,0.4)'),
        fontSize: 12,
    },
    
    // Actions
    actions: {
        flexDirection: 'row',
        padding: 12,
        gap: 8,
        borderTopWidth: 1,
        borderTopColor: uiVar('rs-line-hair', 'rgba(255,255,255,0.06)'),
    },
    actionButton: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 12,
        paddingHorizontal: 12,
        borderRadius: 10,
        backgroundColor: uiVar('rs-fill-wash2', 'rgba(255,255,255,0.08)'),
        gap: 6,
    },
    selectButton: {
        backgroundColor: uiVar('rs-fill-select', 'rgba(99, 102, 241, 0.15)'),
    },
    selectButtonActive: {
        backgroundColor: uiVar('rs-fill-pos-wash', 'rgba(16, 185, 129, 0.15)'),
    },
    actionText: {
        color: uiVar('rs-text-ink2', 'rgba(255,255,255,0.8)'),
        fontSize: 14,
        fontWeight: '600',
    },
    actionTextActive: {
        color: uiVar('rs-text-pos', '#10B981'),
    },
    betNowButtonActive: {
        backgroundColor: uiVar('rs-fill-pos-wash', 'rgba(16, 185, 129, 0.15)'),
        borderWidth: 1,
        borderColor: uiVar('rs-line-pos', 'rgba(16, 185, 129, 0.3)'),
    },
    betNowButtonHovered: {
        backgroundColor: uiVar('rs-fill-pos-wash', 'rgba(16, 185, 129, 0.28)'),
        borderColor: uiVar('rs-line-pos', 'rgba(16, 185, 129, 0.55)'),
    },
    betNowTextActive: {
        color: uiVar('rs-text-pos', '#10B981'),
    },
    selectButtonHovered: {
        backgroundColor: uiVar('rs-fill-select', 'rgba(99, 102, 241, 0.28)'),
        borderWidth: 1,
        borderColor: uiVar('rs-line-ink', 'rgba(99, 102, 241, 0.5)'),
    },
});
